/**
 * DuoCelebration - Emotional celebration when partner link succeeds
 * Shows confetti + welcome reveal panel + guided first actions
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Globe, Sparkles, Play, Users, MapPin, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import confetti from "canvas-confetti";

const DUO_REVEALS = [
  { icon: Globe, title: "Shared Heatmap", desc: "See where you've both been", color: "text-blue-500 bg-blue-500/10" },
  { icon: Users, title: "Couple Dashboard", desc: "Your combined stats", color: "text-pink-500 bg-pink-500/10" },
  { icon: Sparkles, title: "Compatibility", desc: "Your travel style match", color: "text-violet-500 bg-violet-500/10" },
  { icon: Play, title: "Yearly Replay", desc: "Replay adventures together", color: "text-emerald-500 bg-emerald-500/10" },
];

export default function DuoCelebration({ partnerName, hasDuoAccess, onDismiss, isSimulation = false }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("confetti"); // confetti → reveal → actions
  const [cardsVisible, setCardsVisible] = useState(0);

  const fireConfetti = useCallback(() => {
    const defaults = { startVelocity: 20, spread: 360, ticks: 80, zIndex: 99999 };
    const colors = ["#ec4899", "#f43f5e", "#e11d48", "#fb7185", "#f9a8d4"];

    confetti({ ...defaults, particleCount: 60, origin: { x: 0.3, y: 0.6 }, colors });
    setTimeout(() => confetti({ ...defaults, particleCount: 60, origin: { x: 0.7, y: 0.6 }, colors }), 200);
    setTimeout(() => confetti({ ...defaults, particleCount: 40, origin: { x: 0.5, y: 0.4 }, colors }), 400);
  }, []);

  useEffect(() => {
    // Phase 1: Confetti burst
    fireConfetti();
    const t1 = setTimeout(() => setPhase("reveal"), 1200);
    // Phase 2: Stagger reveal cards
    const t2 = setTimeout(() => setCardsVisible(1), 1500);
    const t3 = setTimeout(() => setCardsVisible(2), 1800);
    const t4 = setTimeout(() => setCardsVisible(3), 2100);
    const t5 = setTimeout(() => setCardsVisible(4), 2400);
    // Phase 3: Show action CTAs
    const t6 = setTimeout(() => setPhase("actions"), 3000);

    return () => [t1, t2, t3, t4, t5, t6].forEach(clearTimeout);
  }, [fireConfetti]);

  const handleExploreDuo = () => {
    if (onDismiss) onDismiss();
    navigate("/dashboard");
  };

  const handleAddTrip = () => {
    if (onDismiss) onDismiss();
    navigate("/dashboard");
  };

  const handleUpgrade = () => {
    if (onDismiss) onDismiss();
    navigate("/pricing");
  };

  return (
    <div
      className="fixed inset-0 z-[99998] bg-background flex flex-col items-center justify-center p-6 overflow-y-auto"
      style={{ animation: "fadeIn 0.4s ease" }}
      data-testid="duo-celebration"
    >
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes slideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {/* Avatar merge */}
      <div className="flex items-center justify-center mb-6">
        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center border-3 border-card z-10">
          <Users className="w-8 h-8 text-primary" />
        </div>
        <div className="-ml-4 w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center border-3 border-card">
          <Heart className="w-8 h-8 text-white" />
        </div>
      </div>

      {/* Welcome text */}
      <h1 className="text-2xl sm:text-3xl font-serif font-bold text-foreground text-center mb-1">
        Welcome to your Duo journey
      </h1>
      <p className="text-muted-foreground text-center text-sm mb-6">
        Your shared adventures with {partnerName || "your partner"} now live in one place.
      </p>

      {/* Feature reveal cards */}
      {phase !== "confetti" && (
        <div className="grid grid-cols-2 gap-3 w-full max-w-sm mb-8">
          {DUO_REVEALS.map((item, idx) => {
            const Icon = item.icon;
            const visible = idx < cardsVisible;
            return (
              <Card
                key={idx}
                className="border-0"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(20px)",
                  transition: "all 0.4s ease",
                }}
              >
                <CardContent className="p-3 text-center">
                  <div className={`w-10 h-10 rounded-full ${item.color} flex items-center justify-center mx-auto mb-1.5`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <p className="text-xs font-medium text-foreground">{item.title}</p>
                  <p className="text-[10px] text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Action CTAs */}
      {phase === "actions" && (
        <div className="w-full max-w-sm space-y-3" style={{ animation: "slideUp 0.5s ease" }}>
          {hasDuoAccess ? (
            <>
              <Button
                onClick={handleAddTrip}
                className="w-full h-12 bg-gradient-to-r from-pink-500 to-rose-500 hover:opacity-90 text-white font-semibold rounded-xl"
                data-testid="first-shared-trip-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add your first shared trip
              </Button>
              <Button variant="outline" onClick={handleExploreDuo} className="w-full rounded-xl" data-testid="explore-duo-dashboard-btn">
                <MapPin className="w-4 h-4 mr-2" />
                Explore Duo Dashboard
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleUpgrade}
                className="w-full h-12 bg-gradient-to-r from-pink-500 to-rose-500 hover:opacity-90 text-white font-semibold rounded-xl"
                data-testid="unlock-duo-btn"
              >
                <Heart className="w-5 h-5 mr-2" />
                Learn more on duolyst.co.uk
              </Button>
              <Button variant="outline" onClick={handleExploreDuo} className="w-full rounded-xl">
                Go to Dashboard
              </Button>
            </>
          )}

          {isSimulation && (
            <Button variant="ghost" onClick={onDismiss} className="w-full text-xs text-muted-foreground">
              Close Simulation
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
